import _ from 'lodash';
import { batchActions } from 'redux-batched-actions';

import { sortNodes } from '@zapier/product-editor';
import {
  buildEntityActions,
  buildEntityApi,
  buildEntitySelectors,
  entityStatuses,
} from '@zapier/toolbox-redux-framework';
import { getJson, postJson } from '@zapier/toolbox-browser-fetch';

import { emptyObject, ensureArray, matchesQuery } from 'app/common/CommonUtils';
import partialWithDefault from './utils/partialWithDefault';

export const type = 'zap';

export const api = buildEntityApi(type, '/api/v3/zaps', zapApi => {
  zapApi.moveZaps = (toAccountId, zapIds) => {
    return postJson(api.collectionEndpoint('/move'), {
      target_account_id: toAccountId,
      zap_ids: zapIds,
    });
  };

  zapApi.loadUsage = () => getJson(api.collectionEndpoint('/usage'));
});

export const USAGE_BY_ACCOUNT = 'usage_by_account';
export const TOTAL_COUNT = 'total_count';
export const DRAFT_COUNT = 'draft_count';
export const ON_COUNT = 'on_count';
const MOVING_STATUS = 'movingStatus';

export const selectors = buildEntitySelectors(type, sel => {
  sel.title = _.partial(sel.dataItem, 'title');
  sel.titleMatches = (query, entity) => matchesQuery(query, sel.title(entity));
  sel.folderIds = partialWithDefault(sel.dataItem, ['folder_ids'], []);
  sel.firstFolderId = _.flow(sel.folderIds, _.first);
  sel.folderId = partialWithDefault(sel.firstFolderId, [], 0);
  sel.isInFolder = _.partial(sel.matchesSelector, sel.folderId);
  sel.zapState = _.partial(sel.dataItem, 'state');
  sel.hasZapState = _.partial(sel.matchesSelector, sel.zapState);
  sel.isOn = _.partial(sel.hasZapState, 'on');
  sel.isOff = _.partial(sel.hasZapState, 'off');
  sel.isDraft = _.partial(sel.hasZapState, 'draft');
  sel.isSelectedForMoving = partialWithDefault(
    sel.stateItem,
    ['isSelectedForMoving'],
    false
  );
  sel.nodes = partialWithDefault(sel.dataItem, ['nodes'], emptyObject);
  sel.ownerId = partialWithDefault(sel.dataItem, ['owner_id']);
  sel.isOwnedBy = partialWithDefault(sel.matchesSelector, [sel.ownerId], false);
  sel.nodeApis = entity =>
    sortNodes(sel.nodes(entity)).map(node => node.selected_api || 'unknown');
  sel.all.usageByAccount = partialWithDefault(
    sel.all.dataItem,
    [USAGE_BY_ACCOUNT],
    {}
  );
  sel.all.accountIdsWithUsageData = _.flow(
    sel.all.usageByAccount,
    Object.keys,
    (ids = []) => ids.map(id => parseInt(id, 10))
  );
  sel.all.numOnZapsForAccount = (accountId, state) =>
    sel.all.dataItem([USAGE_BY_ACCOUNT, accountId, ON_COUNT], state) || 0;
  sel.all.numTotalZapsForAccount = (accountId, state) =>
    sel.all.dataItem([USAGE_BY_ACCOUNT, accountId, TOTAL_COUNT], state) || 0;
  sel.all.numDraftZapsForAccount = (accountId, state) =>
    sel.all.dataItem([USAGE_BY_ACCOUNT, accountId, DRAFT_COUNT], state) || 0;
  sel.all.canMoveZapsFromAccount = (accountId, state) => {
    const totalZaps = sel.all.numTotalZapsForAccount(accountId, state);
    const draftZaps = sel.all.numDraftZapsForAccount(accountId, state);
    return totalZaps - draftZaps > 0;
  };
  sel.all.canMoveZapsToAccount = (accountId, state) => {
    const allAccountIds = sel.all.accountIdsWithUsageData(state);
    const allOtherAccountIds = _.without(allAccountIds, accountId);
    return allOtherAccountIds.some(id =>
      sel.all.canMoveZapsFromAccount(id, state)
    );
  };
  sel.all.accountsWithMovableZaps = state =>
    sel.all
      .accountIdsWithUsageData(state)
      .filter(id => sel.all.canMoveZapsFromAccount(id, state));
  sel.all.inFolder = _.partial(sel.all.filterBy, sel.isInFolder);
  sel.all.movingZapsQuery = partialWithDefault(
    sel.all.stateItem,
    ['movingZapsQuery'],
    ''
  );
  sel.all.movingStatus = _.partial(sel.all.stateItem, MOVING_STATUS);
  sel.all.isMoving = _.flow(sel.all.movingStatus, Boolean);
  sel.all.movedCount = _.partial(sel.all.stateItem, 'movedCount');
  sel.all.zapsSelectedForMoving = _.partial(
    sel.all.filterBy,
    sel.isSelectedForMoving
  );
});

export const actions = buildEntityActions(
  type,
  selectors,
  api,
  entityActions => {
    entityActions.updateMovingZapsQuery = _.partial(
      entityActions.updateCollectionStateItem,
      'movingZapsQuery'
    );

    entityActions.updateMovedCount = _.partial(
      entityActions.updateCollectionStateItem,
      'movedCount'
    );

    entityActions.updateIsSelectedForMoving = _.partial(
      entityActions.updateEntityStateItem,
      'isSelectedForMoving'
    );

    entityActions.deselectAllZapsForMoving = () => (dispatch, getState) => {
      const selectedZapIds = selectors.all
        .zapsSelectedForMoving(getState())
        .map(selectors.id);
      dispatch(entityActions.selectZapsForMoving(false, selectedZapIds));
    };

    entityActions.selectZapsForMoving = (shouldAdd, zapIds) => dispatch => {
      const batchedActions = ensureArray(zapIds).map(zapId =>
        entityActions.updateIsSelectedForMoving(shouldAdd, zapId)
      );
      dispatch(batchActions(batchedActions));
    };

    entityActions.selectFolderForMoving = (
      shouldAdd,
      id,
      includeDrafts = false
    ) => (dispatch, getState) => {
      const state = getState();
      const query = selectors.all.movingZapsQuery(state);
      const zapIds = selectors.all
        .inFolder(id, state)
        .filter(
          includeDrafts ? _.stubTrue : _.negate(_.partial(selectors.isDraft))
        )
        .filter(_.partial(selectors.titleMatches, query))
        .map(selectors.id);
      dispatch(entityActions.selectZapsForMoving(shouldAdd, zapIds));
    };

    entityActions.moveZaps = (toAccountId, zapIds) => dispatch => {
      dispatch(
        entityActions.updateCollectionStateItem(
          MOVING_STATUS,
          entityStatuses.SAVING
        )
      );
      return api
        .moveZaps(toAccountId, zapIds)
        .then(() => {
          dispatch(
            batchActions([
              entityActions.updateCollectionStateItem(
                MOVING_STATUS,
                entityStatuses.SUCCESS
              ),
              // Since we're removing the moved entities we need to store
              // how many of them we moved for the UI.
              entityActions.updateMovedCount(zapIds.length),
              entityActions.deleteCollectionEntities(zapIds),
            ])
          );
        })
        .catch(() => {
          dispatch(
            entityActions.updateCollectionStateItem(
              MOVING_STATUS,
              entityStatuses.FAILURE
            )
          );
        });
    };

    entityActions.resetMovingStatus = _.partial(
      entityActions.updateCollectionStateItem,
      MOVING_STATUS,
      undefined
    );

    entityActions.loadUsage = () => dispatch =>
      api.loadUsage().then(usageData => {
        dispatch(
          entityActions.updateCollectionData({
            [USAGE_BY_ACCOUNT]: usageData || {},
          })
        );
      });
  }
);
